import { useMemo } from "react";
import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import { AvailableFilter } from "@/models/filter";
import { Content } from "@/models/document";
import { normalizeElasticsearchTypeToFilterType } from "@/utils/normalize-es-types";
import { getDefaultNormalizedFields } from "@/utils/normalized-model-fields";
import logger from "@/utils/logger";
import { hasSelectedWorkspace } from "./core";

export const useMappings = (enabled: boolean = true) =>
  useApiQuery<AvailableFilter[]>(
    {
      url: `workspaces/mapping`,
      method: "GET",
    },
    {
      // Only enable if workspace is selected
      enabled: enabled && hasSelectedWorkspace(),
      staleTime: 5 * 60 * 1000, // 5 minutes — schema-like data, nearly static
      retry: (failureCount, error) => {
        if ([404, 500].includes(error?.response?.status ?? 0)) {
          return false;
        }
        return failureCount < 2;
      },
    }
  );

/**
 * Parse un champ préfixé (ex: "num$likes") et retourne le nom et le type
 * Format: type$fieldName où type peut être: num, text, date, bool, obj, geo, etc.
 */
const parseFieldPrefix = (prefixedField: string): { key: string; type: string } => {
  // Mapping des préfixes vers les types Elasticsearch
  const prefixToType: Record<string, string> = {
    num: "number",
    text: "text",
    kw: "keyword",
    date: "date",
    bool: "boolean",
    obj: "object",
    geo: "geo_point",
    nested: "nested",
  };

  // Regex pour parser le format: type$fieldName ou obj$parent.type$child
  // On doit gérer les champs imbriqués comme "obj$actor.text$name"
  const parts: string[] = [];
  let lastType = "text"; // Type par défaut

  // Parser chaque segment du champ
  const segments = prefixedField.split(".");
  for (const segment of segments) {
    const dollarIndex = segment.indexOf("$");
    if (dollarIndex !== -1) {
      const prefix = segment.substring(0, dollarIndex);
      const fieldName = segment.substring(dollarIndex + 1);
      if (prefixToType[prefix]) {
        lastType = prefixToType[prefix];
      }
      parts.push(fieldName);
    } else {
      parts.push(segment);
    }
  }

  return {
    key: parts.join("."),
    type: lastType,
  };
};

/**
 * Transforme la liste de champs préfixés en tableau d'AvailableFilter
 * Conserve le format type$name dans la clé pour les requêtes backend
 */
const transformFieldsToAvailableFilters = (
  fields: string[]
): AvailableFilter[] => {
  if (!fields || !Array.isArray(fields)) return [];

  return fields.map((field) => {
    const { type } = parseFieldPrefix(field);
    return {
      key: field, // Garder le format type$name (ex: "num$likes")
      type: normalizeElasticsearchTypeToFilterType(type),
      enabled: true,
    };
  });
};

/**
 * Récupère la liste des champs disponibles depuis le endpoint /fields
 * Les champs sont retournés avec leurs préfixes de type (ex: "num$likes", "text$username")
 * et sont automatiquement transformés en AvailableFilter[]
 */
export const useFields = (enabled: boolean = true) => {
  const queryResult = useApiQuery<string[]>(
    {
      url: `workspaces/fields`,
      method: "GET",
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes — schema-like data, nearly static
      retry: (failureCount, error) => {
        if ([404, 500].includes(error?.response?.status ?? 0)) {
          return false;
        }
        return failureCount < 2;
      },
    }
  );

  // Transformer les champs préfixés en AvailableFilter[]
  const transformedData = useMemo(() => {
    if (!queryResult.data) return undefined;
    return transformFieldsToAvailableFilters(queryResult.data);
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
    // Exposer aussi les données brutes si besoin
    rawFields: queryResult.data,
  };
};

/**
 * Type de la réponse brute du backend pour les facets
 */
type FacetsResponse = {
  facets: {
    [fieldKey: string]: {
      type: "range" | "terms";
      min?: number | string;
      max?: number | string;
      values?: Array<{
        key: string;
        doc_count: number;
      }>;
    };
  };
};

/**
 * Transforme la réponse du backend en format AvailableFilter[]
 */
const transformFacetsResponse = (
  response: FacetsResponse | undefined
): AvailableFilter[] => {
  if (!response || !response.facets) return [];

  return Object.entries(response.facets).map(([key, facet]) => {
    // Le type sera déterminé depuis le mapping dans useAvailableFilters
    // Ici, on fait juste une approximation basée sur le type de facet
    // mais le vrai type ES viendra du mapping
    const estimatedType = facet.type === "terms" ? "text" : "number";
    
    const availableFilter: AvailableFilter = {
      key,
      enabled: true,
      type: estimatedType, // Type estimé, sera remplacé par le type du mapping si disponible
    };

    if (facet.type === "terms" && facet.values) {
      // Pour les facets de type "terms", convertir les values en options
      availableFilter.options = facet.values.map((v) => ({
        key: v.key,
        docCount: v.doc_count,
      }));
    } else if (facet.type === "range") {
      // Pour les facets de type "range", utiliser min et max
      if (facet.min !== undefined) {
        availableFilter.min = facet.min;
      }
      if (facet.max !== undefined) {
        availableFilter.max = facet.max;
      }
    }

    return availableFilter;
  });
};

/**
 * Récupère les facets (agrégations) Elasticsearch pour les filtres disponibles
 * Les facets incluent les valeurs disponibles, min/max, etc. basés sur les données réelles
 */
export const useFacets = (
  trackers_id?: string | string[],
  enabled: boolean = true
) => {
  // Construire le body de la requête POST
  const trackersIdList = trackers_id
    ? Array.isArray(trackers_id)
      ? trackers_id
      : [trackers_id]
    : [];

  const bodyData: Record<string, any> = {
    trackers_id: trackersIdList, // Toujours un array dans le body
  };

  const queryResult = useApiQuery<FacetsResponse>(
    {
      url: `workspaces/facets`,
      method: "POST",
      data: bodyData,
    },
    {
      // Only enable if workspace is selected
      enabled: enabled && hasSelectedWorkspace(),
      // Ne pas retry si la requête échoue (facets sont optionnels)
      retry: false,
      // Timeout pour éviter de rester bloqué
      staleTime: 30000, // 30 secondes
    }
  );

  // Transformer la réponse
  const transformedData = useMemo(() => {
    if (!queryResult.data) return undefined;
    return transformFacetsResponse(queryResult.data);
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
  };
};

/**
 * Combine les champs du workspace (via /fields) avec les facets pour obtenir les filtres disponibles
 * avec toutes les informations nécessaires (options, min/max, etc.)
 * 
 * Utilise le endpoint workspaces/fields comme source principale pour les champs disponibles.
 * Les facets sont basés sur les données réelles dans Elasticsearch et fournissent :
 * - Les valeurs disponibles pour les champs text (options)
 * - Les min/max pour les champs numériques et dates
 * - Les comptages de documents pour chaque valeur
 */
export const useAvailableFilters = (
  trackers_id?: string | string[],
  enabled: boolean = true
) => {
  const { 
    data: fields, 
    isLoading: isLoadingFields,
    isError: isErrorFields 
  } = useFields(enabled);
  
  // Toujours appeler facets (même si enabled est false, on veut au moins essayer)
  // enabled contrôle seulement si on attend la réponse avant d'afficher
  const { 
    data: facets, 
    isLoading: isLoadingFacets,
    isError: isErrorFacets 
  } = useFacets(trackers_id, true); // Toujours enabled pour facets

  // Obtenir les champs normalisés par défaut (type DOC)
  const defaultNormalizedFields = useMemo(() => {
    return getDefaultNormalizedFields();
  }, []);

  // Combiner les fields avec les facets
  const availableFilters = useMemo(() => {
    // Si on a des fields, les utiliser comme base
    if (fields && Array.isArray(fields) && fields.length > 0) {
      // Si on a des facets, les combiner avec les fields
      if (facets && Array.isArray(facets) && facets.length > 0) {
        // Créer un map des facets par clé pour un accès rapide
        const facetsMap = new Map<string, AvailableFilter>();
        facets.forEach((facet) => {
          facetsMap.set(facet.key, facet);
        });

        // Créer un map des fields par clé pour vérifier les correspondances
        const fieldsMap = new Map<string, AvailableFilter>();
        fields.forEach((field) => {
          fieldsMap.set(field.key, field);
        });

        // Combiner : utiliser les fields pour la structure de base, et les facets pour les données enrichies
        const combined = fields.map((field) => {
          const facet = facetsMap.get(field.key);
          if (facet) {
            // Fusionner : garder le type des fields (c'est la source de vérité pour le type ES),
            // mais utiliser les options/min/max des facets (données réelles)
            return {
              ...field,
              type: field.type,
              options: facet.options || field.options,
              min: facet.min !== undefined ? facet.min : field.min,
              max: facet.max !== undefined ? facet.max : field.max,
              enabled: facet.enabled !== undefined ? facet.enabled : field.enabled,
            };
          }
          return field;
        });

        // Ajouter les facets qui ne sont pas dans les fields (nouveaux champs)
        facets.forEach((facet) => {
          if (!fieldsMap.has(facet.key)) {
            combined.push(facet);
          }
        });

        return combined;
      }

      // Si pas de facets, retourner juste les fields
      return fields;
    }

    // Si pas de fields mais qu'on a des facets, retourner les facets enrichis avec les champs par défaut
    if (facets && Array.isArray(facets) && facets.length > 0) {
      // Créer un map des facets par clé
      const facetsMap = new Map<string, AvailableFilter>();
      facets.forEach((facet) => {
        facetsMap.set(facet.key, facet);
      });

      // Combiner les champs par défaut avec les facets
      const combined = defaultNormalizedFields.map((defaultField) => {
        const facet = facetsMap.get(defaultField.key);
        if (facet) {
          // Enrichir le champ par défaut avec les données du facet
          return {
            ...defaultField,
            options: facet.options,
            min: facet.min,
            max: facet.max,
          };
        }
        return defaultField;
      });

      // Ajouter les facets qui ne sont pas dans les champs par défaut
      facets.forEach((facet) => {
        const hasDefault = defaultNormalizedFields.some((df) => df.key === facet.key);
        if (!hasDefault) {
          combined.push(facet);
        }
      });

      return combined;
    }

    // Si ni fields ni facets, retourner les champs normalisés par défaut (type DOC)
    return defaultNormalizedFields;
  }, [fields, facets, defaultNormalizedFields]);

  // Ne pas bloquer sur les facets - si les fields sont chargés, on peut retourner les données
  // Les facets sont un enrichissement optionnel qui peut échouer ou être bloqué
  // On considère que c'est chargé si :
  // - Les fields sont chargés OU
  // - On a des facets OU
  // - On a toujours les champs par défaut (type DOC) disponibles
  // Si on a des données (même les champs par défaut), on ne considère plus comme "loading"
  // Les champs par défaut sont toujours disponibles, donc on n'est jamais vraiment en loading
  // availableFilters contient toujours au moins les champs par défaut, donc isLoading sera toujours false
  const isLoading = enabled && isLoadingFields && !fields && !facets && defaultNormalizedFields.length === 0;
  
  return {
    data: availableFilters,
    isLoading: isLoading,
    isError: isErrorFields, // Ne pas considérer l'erreur des facets comme bloquante
  };
};

export const useImportDocuments = () =>
  useApiMutation<{}, { _: unknown[] }, { trackerId: string }>({
    url: "trackers/{trackerId}/import_documents",
    method: "POST",
  });

// Re-export resource-group hooks
export {
  useResourceGroups,
  useResourceGroup,
  useCreateResourceGroup,
  useUpdateResourceGroup,
  useDeleteResourceGroup,
} from "./resource-group";

// Re-export report hooks for PDF export
export {
  useReports,
  useReport,
  useCreateReport,
  useDeleteReport,
  useGenerateReport,
  downloadBlob,
  downloadReport,
  formatCronExpression,
  CRON_PRESETS,
} from "./report";

export const useDocumentsSearch = (
  param: {
    date_range?: string;
    limit?: number;
    query?: Record<string, unknown>;
  },
  queryConfig?: { enabled?: boolean; staleTime?: number }
) => {
  // Body de la requête POST - query contient la query_string ES avec les filtres tracker inclus
  const bodyData: Record<string, unknown> = {
    query: param.query || {},
  };

  // Debug: Afficher la query string complète
  const baseURL = import.meta.env?.VITE_API_CROWLINGO || "";
  const urlPath = "workspaces/documents/search";
  const fullURL = `${baseURL}/${urlPath}`;

  logger.api("=== DOCUMENTS SEARCH QUERY STRING DEBUG ===");
  logger.api("Full URL:", fullURL);
  logger.api("Method: POST");
  logger.api("Request Body:", JSON.stringify(bodyData, null, 2));
  logger.api("===========================================");
  
  return useApiQuery<Content[]>(
    {
      url: "workspaces/documents/search",
      method: "POST",
      data: bodyData,
    },
    queryConfig
  );
};
